// ts-check
import BaseAPI from './BaseAPI';
import _ from 'lodash';
import {
  createAggregatedRequestData,
  convertTsiDataToGraphData
} from './TelemetryApi/TimeseriesWrapper';

// eslint-disable-next-line no-unused-vars
import * as CoreApiTypes from './CoreApi.js';

/**
 * @typedef {import('utils/types').AssetType} AssetType
 */
/**
 * @typedef {import('utils/types').Provider} Provider
 */


/**
 * Enum for provider types
 * @readonly
 * @enum {string}
 */
// eslint-disable-next-line no-unused-vars
export const BTProviderType = Object.freeze({
  iothub: 'iothub',
  import: 'import'
});

/**
 * Enum for possible transports for providers
 * @readonly
 * @enum {string}
 */
// eslint-disable-next-line no-unused-vars
export const BTProviderTransport = Object({
  http: 'http'
});

/**
 * Enum for possible management rights for users and groups
 * @readonly
 * @enum {string}
  */
// export const privileges = Object.freeze({
//   any: 'any',
//   asset_management: 'asset_management',
//   dashboard_management: 'dashboard_management',
//   user_management: 'user_management',
//   tree_management: 'tree_management',
//   model_management: 'model_management',
//   alarm_management: 'alarm_management',
//   org_management: 'organization_management',
//   provider_management: 'provider_management',
//   provider_client_management: 'provider_client_management'
// });

/**
 * Enum for possible permissions for users and groups
 * @readonly
 */
export const permissions = Object.freeze({
  view: 'view',
  manage: 'manage',
  delete: 'delete',
  share: 'share'
});

class CoreApi extends BaseAPI {
  /**
   *
   * @param {*} cancelSignal
   * @returns {Array CoreApiTypes.Provider}
   */
  getProviders = cancelSignal => this.requestGet('providers', null, cancelSignal);
  /**
   *
   * @param {object} cancelSignal - cancelSignal
   * @param {string} providerId - id of the provider
   * @returns {CoreApiTypes.BTProvider}
   */
  getProvider = (cancelSignal, providerId) => this.requestGet(`providers/${providerId}`, null, cancelSignal);
  deleteProvider = (cancelSignal, providerId) => this.requestDelete(`providers/${providerId}`, null, cancelSignal);
  updateProvider = (cancelSignal, providerId) => this.requestPut(`providers/${providerId}`, null, cancelSignal);

  getProviderClients = (cancelSignal, providerId) => this.requestGet(`providers/${providerId}/clients`, null, cancelSignal);
  getProviderClient = (cancelSignal, providerId, providerClientId) => this.requestGet(`providers/${providerId}/clients/${providerClientId}`, null, cancelSignal);
  createProviderClient = (cancelSignal, providerId, providerClientData) => this.requestPost(`providers/${providerId}/clients`, providerClientData, cancelSignal);
  deleteProviderClient = (cancelSignal, item) => this.requestDelete(`providers/${item.providerId}/clients/${item.providerClientId}`, null, cancelSignal);
  updateProviderClient = (cancelSignal, providerId, providerClientId, providerClientData) => this.requestPatch(`providers/${providerId}/clients/${providerClientId}`, providerClientData, cancelSignal);
  provisionProviderClient = (cancelSignal, providerId, providerClientId, providerClientTemplate) => this.requestPost(`providers/${providerId}/clients/${providerClientId}/provision`, providerClientTemplate, cancelSignal);

  sendDirectMethod = (cancelSignal, providerId, providerClientId, message) => this.requestPost(`providers/${providerId}/clients/${providerClientId}`, message, cancelSignal);
  getReportedProperties = (cancelSignal, providerId, providerClientId) => this.requestGet(`providers/${providerId}/clients/${providerClientId}/reportedProperties`, null, cancelSignal);
  getDesiredProperties = (cancelSignal, providerId, providerClientId) => this.requestGet(`providers/${providerId}/clients/${providerClientId}/desiredProperties`, null, cancelSignal);
  updateDesiredProperties = (cancelSignal, providerId, providerClientId, desiredProperties) => this.requestPost(`providers/${providerId}/clients/${providerClientId}/desiredProperties`, desiredProperties, cancelSignal);


  /**
   *
   * @param {*} cancelSignal
   * @param {string} providerId - providerClientId
   * @param {object} tagFilter - object with tags as keys and matches as values
   * @returns
   */
  getProviderClientTemplates = (cancelSignal, tagFilter) => this.requestGet('providertemplates', tagFilter, cancelSignal);
  getProviderClientTemplate = (cancelSignal, templateId) => this.requestGet(`providertemplates/${templateId}`, null, cancelSignal);
  createProviderClientTemplate = (cancelSignal, templateData) => this.requestPost('providertemplates', templateData, cancelSignal);
  updateProviderClientTemplate = (cancelSignal, templateId, templateData) => this.requestPut(`providertemplates/${templateId}`, templateData, cancelSignal);
  deleteProviderClientTemplate = (cancelSignal, templateId) => this.requestDelete(`providertemplates/${templateId}`, null, cancelSignal);

  getAssets = (cancelSignal) => this.requestGet('assets', null, cancelSignal);
  createAsset = (cancelSignal, assetData) => this.requestPost('assets', assetData, cancelSignal);
  createDefaultAsset = (cancelSignal, assetData, providerId, providerClientData) => {
    const assetsPromise = this.createAsset(cancelSignal, assetData);
    const providerPromise = this.createProviderClient(cancelSignal, providerId, providerClientData);
    return Promise.all([assetsPromise, providerPromise])
      .then(() => {
        const bindData = {
          autobindEndpointInstanceId: `${providerId}::${providerClientData.id}`
        };
        this.bindAsset(cancelSignal, assetData.id, bindData);
      });
  };
  getAsset = (cancelSignal, assetId) => this.requestGet(`assets/${assetId}`, null, cancelSignal);
  getAssetsByModel = (cancelSignal, modelId) => this.requestGet(`assets/model/${encodeURIComponent(modelId)}`, null, cancelSignal);
  getAssetsByModels = (cancelSignal, assetModelList) => {
    const promises = _.map(assetModelList, assetModelId => this.getAssetsByModel(cancelSignal, assetModelId));
    return Promise.all(promises).then(results => {
      return _.unionBy(_.flatten(results), 'id');
    });
  };

  updateAsset = (cancelSignal, assetId, assetData) => this.requestPatch(`assets/${assetId}`, assetData, cancelSignal);
  deleteAsset = (cancelSignal, assetId) => this.requestDelete(`assets/${assetId}`, null, cancelSignal);
  resetAsset = (cancelSignal, assetId) => this.requestPost(`assets/${assetId}/reset`, null, cancelSignal);
  bindAsset = (cancelSignal, assetId, bindingDescription) => this.requestPost(`assets/${assetId}/bind`, bindingDescription, cancelSignal);
  getAssetAndProviders = (cancelSignal, assetId) => {
    let providerMap = {};
    const assetPromise = this.getAsset(cancelSignal, assetId);
    return assetPromise.then(/** @type {AssetType[]} */asset => {
      const modelPromise = this.getModel(cancelSignal, asset.modelId);
      const uniqEndpoints = _.uniq(asset?.bindingData?.autobindRemoteInstanceIds);
      const providerClientPromises = _.map(uniqEndpoints, endpoint => {
        if (_.includes(endpoint, '::')) {
          const [providerId, providerClientId] = endpoint.split('::');
          providerMap[providerClientId] = providerId;
          return this.getProviderClient(cancelSignal, providerId, providerClientId);
        }
        providerMap[endpoint] = null;
        return this.getAsset(cancelSignal, endpoint);
      });
      const providerPromises = _.map(providerMap, providerId => (providerId) ? this.getProvider(cancelSignal, providerId) : null);
      return Promise.all([modelPromise, ...providerClientPromises]).then(result => {
        return Promise.all(providerPromises).then(providers => {
          const clients = _.slice(result, 1);
          return {
            providerClients: _.map(clients, client => ({ ...client, provider: providerMap[client.id] })),
            providers: providers,
            asset: {
              ...asset,
              model: _.first(result)
            }
          };
        });
      });
    });
  };
  migrateAssetBinding = (cancelSignal, assetIds, binding) => this.requestPost(
    'bindings/migrate',
    {
      type: 'BindingModel',
      assetIds,
      bindingModelId: binding.id
    },
    cancelSignal
  );

  migrateAssetModel = (cancelSignal, migrationData) => this.requestPost('assets/migrate', migrationData, cancelSignal);


  validateAssetBinding = (cancelSignal, binding) => {
    const assetListPromise = this.getAssetsByModel(cancelSignal, binding.assetModelId);
    return Promise.all([assetListPromise]).then(results => {
      if (_.isEmpty(results[0])) {
        return {
          assetIds: [],
          validation: {}
        };
      }
      const assetIds = _.map(results[0], asset => asset.id);
      const bindingData = {
        type: 'BindingModel',
        assetIds,
        bindingModelId: binding.id
      };
      const validatePromise = this.requestPost('/bindings/migrate/validate', bindingData, cancelSignal);
      return Promise.all([validatePromise]).then(results => {
        return {
          assetIds,
          validation: results[0]
        };
      });
    });
  };

  executeAssetCommand = (cancelSignal, assetId, command) => this.requestPost(`assets/${assetId}/executeCommand`, command, cancelSignal);
  getAssetReportedProperties = (cancelSignal, assetId) => this.requestGet(`assets/${assetId}/properties/reported`, null, cancelSignal);
  getAssetDesiredProperties = (cancelSignal, assetId) => this.requestGet(`assets/${assetId}/properties/desired`, null, cancelSignal);
  updateAssetDesiredProperties = (cancelSignal, assetId, desiredProperties) => this.requestPut(`assets/${assetId}/properties/desired`, desiredProperties, cancelSignal);

  // getTrees = (cancelSignal) => this.requestGet('trees', null, cancelSignal);
  createTree = (cancelSignal, treeData) => this.requestPost('trees', treeData, cancelSignal);
  // getTree = (cancelSignal, treeId) => this.requestGet(`trees/${treeId}`, null, cancelSignal);
  deleteTree = (cancelSignal, treeId) => this.requestDelete(`trees/${treeId}`, null, cancelSignal);
  getTreeItems = (cancelSignal, treeId) => this.requestGet(`trees/${treeId}/items`, null, cancelSignal);
  createTreeEdge = (cancelSignal, treeEdgeData) => this.requestPost('trees/edges', treeEdgeData, cancelSignal);
  deleteTreeEdge = (cancelSignal, treeEdgeId) => this.requestDelete(`trees/edges/${treeEdgeId}`, null, cancelSignal);
  deleteTreeCollectionDocument = (cancelSignal, collectionId, documentId) => this.requestDelete(`trees/documents/${collectionId}/${documentId}`, null, cancelSignal);

  getDashboards = (cancelSignal) => this.requestGet('dashboards', null, cancelSignal);
  saveDashboard = (cancelSignal, dashboardData) => {
    if (dashboardData.id) {
      return this.requestPatch(`dashboards/${dashboardData.id}`, dashboardData, cancelSignal);
    }
    return this.requestPost('dashboards', dashboardData, cancelSignal);
  };

  getDashboard = (cancelSignal, dashboardId) => this.requestGet(`dashboards/${dashboardId}`, null, cancelSignal);
  deleteDashboard = (cancelSignal, dashboardId) => this.requestDelete(`dashboards/${dashboardId}`, null, cancelSignal);

  /**
   * @typedef  {object} AuthBody
   * @param {string} client_id
   * @param {string} grant_type
   * @param {string} username
   * @param {string} password
   * @param {string} scope
   */
  /**
   * @param {*} cancelSignal
   * @param {AuthBody} authBody - object with username and password
   */
  getOauthToken = (cancelSignal, authBody) => this.requestGet('oauth/token', authBody, cancelSignal);

  getTenants = (cancelSignal) => this.requestGet('tenants', null, cancelSignal);
  getTenant = (cancelSignal, tenantId) => this.requestGet(`tenants/${tenantId}`, null, cancelSignal);

  /**
   * @typedef {object} OrgGroupRelationships
   * @property {string} groupId
   * @property {Permissions} relation
   */
  /**
   * @typedef {object} OrgUserRelationships
   * @property {string} userId
   * @property {Permissions} relation
   */
  /**
   * @typedef {object} OrgResourceRelationships
   * @property {string} resourceId
   * @property {Permissions} relation
   * @property {string} resourceType
   */
  /**
   * @typedef {object} OrgRelationships
   * @property {Array<OrgGroupRelationships>} GroupToOrgRelations
   * @property {Array<OrgUserRelationships>} UserToOrgRelations
   * @property {Array<OrgResourceRelationships>} OrgToResourceRelations
   */
  /**
   * @typedef {object} OrganizationReleations
   * @property {string} parentOrganizationId,
   * @property {string} displayName,
   * @property {string} description,
   * @property {Array<Privileges>} privileges,
   * @property {OrgRelationships} relations
   */
  /**
   * @param {*} cancelSignal
   * @returns {Array<OrganizationReleations>}
   */
  getOrganizations = (cancelSignal) => this.requestGet('organizations', null, cancelSignal);
  /**
   * @param {*} cancelSignal
   * @param {OrganizationReleations} organizationData - object with organization data
   * @returns {Array<OrganizationReleations>}
   * @memberof CoreApi
   */
  createOrganization = (cancelSignal, organizationData) => {
    return this.requestPost('organizations', organizationData, cancelSignal);
  };
  getOrganization = (cancelSignal, organizationId) => this.requestGet(`organizations/${organizationId}`, null, cancelSignal);
  updateOrganization = (cancelSignal, organizationData) => {
    return this.requestPatch(`organizations/${organizationData.id}`, organizationData, cancelSignal);
  };
  deleteOrganization = (cancelSignal, organizationId) => this.requestDelete(`organizations/${organizationId}`, null, cancelSignal);

  /**
   * @typedef {object} OrganizationSettings
   * @property {string} orgId
   * @property {string} logoUrl
   * @property {Array<string>} homeDashboards
   **/
  /**
   * @param {*} cancelSignal
   * @param {string} organizationId - object with organization data
   * @returns {OrganizationSettings}
   * @memberof CoreApi
   * */
  getOrganizationSettings = (cancelSignal, organizationId) => {
    return this.requestGet(`organizations/${organizationId}/settings`, null, cancelSignal);
  };
  updateOrganizationSettings = (cancelSignal, organizationData) => {
    const orgId = organizationData.orgId;
    const orgSettings = _.omit(organizationData, 'orgId');
    return this.requestPut(`organizations/${orgId}/settings`, orgSettings, cancelSignal);
  };

  getUsers = (cancelSignal) => this.requestGet('users', null, cancelSignal);
  createUser = (cancelSignal, userData) => this.requestPost('users', userData, cancelSignal);
  getUser = (cancelSignal, userId) => this.requestGet(`users/${userId}`, null, cancelSignal);
  updateUser = (cancelSignal, userData) => this.requestPatch(`users/${userData.userId}`, userData, cancelSignal);
  deleteUser = (cancelSignal, userId) => this.requestDelete(`users/${userId}`, null, cancelSignal);
  getUserSettings = (cancelSignal, userId) => this.requestGet(`users/${userId}/settings`, null, cancelSignal);
  updateUserSettings = (cancelSignal, userData) => {
    const userId = userData.userId;
    const userSettings = _.omit(userData, 'userId');
    return this.requestPut(`users/${userId}/settings`, userSettings, cancelSignal);
  };

  getGroups = (cancelSignal) => this.requestGet('groups', null, cancelSignal);
  createGroup = (cancelSignal, groupData) => this.requestPost('groups', groupData, cancelSignal);
  getGroup = (cancelSignal, groupId) => this.requestGet(`groups/${groupId}`, null, cancelSignal);
  updateGroup = (cancelSignal, groupData) => {
    return this.requestPatch(`groups/${groupData.id}`, groupData, cancelSignal);
  };
  deleteGroup = (cancelSignal, groupId) => this.requestDelete(`groups/${groupId}`, null, cancelSignal);

  /**
   * @typedef {object} Relationship
   * @property {string} subjectType
   * @property {string} subjectId
   * @property {string} resourceType
   * @property {string} resourceId
   * @property {string} relation
   */

  /**
   * @param {*} cancelSignal
   * @param {Relationship} relationshipData - object with relationship data
   */
  createRelationship = (cancelSignal, relationshipData) => this.requestPost('relations', relationshipData, cancelSignal);

  /**
   * @param {*} cancelSignal
   * @param {Relationship} relationshipData - object with relationship data
   * @returns {Array Relationship}
   */
  findRelationships = (cancelSignal, relationshipData) => {
    return this.requestPost('relations/find', relationshipData, cancelSignal);
  };

  /**
   * @param {*} cancelSignal
   * @param {Relationship} relationshipData - object with relationship data
   */
  deleteRelationship = (cancelSignal, relationshipData) => this.requestDelete(`relations/${relationshipData.subjectType}/${relationshipData.subjectId}/${relationshipData.resourceType}/${relationshipData.resourceId}/${relationshipData.relation}`, null, cancelSignal);

  /**
   * @typedef {object} Permission
   * @property {string} subjectType
   * @property {string} subjectId
   * @property {string} resourceType
   * @property {string} resourceId
   * @property {string} permission
   */
  /**
   * @param {*} cancelSignal
   * @param {Array<Permission>} permissionData - object with permission data
   */
  checkPermission = (cancelSignal, permissionData) => this.requestPost('permissions/check', permissionData, cancelSignal);
  /**
   * @param {*} cancelSignal
   * @param {Permission} permissionData - object with permission data
   */
  lookupResources = (cancelSignal, permissionData) => this.requestPost('permissions/lookup-resource', permissionData, cancelSignal);
  /**
   * @param {*} cancelSignal
   * @param {Permission} permissionData - object with permission data
   */
  lookupSubjects = (cancelSignal, permissionData) => this.requestPost('permissions/lookup-subject', permissionData, cancelSignal);

  /**
   * @typedef {object} Privileges
   * @property {string} subjectType
   * @property {string} subjectId
   * @property {Array<Privileges>} privileges
   * @property {string} orgId
   */
  /**
   * @param {*} cancelSignal
   * @param {Array<Privileges>} privilegesData - object with privileges data
   */
  checkPrivileges = (cancelSignal, privilegesData) => this.requestPost('privileges/check', privilegesData, cancelSignal);


  getModels = (cancelSignal) => this.requestGet('models', null, cancelSignal);
  createModel = (cancelSignal, modelData) => this.requestPost('models', modelData, cancelSignal);
  getModel = (cancelSignal, modelId) => this.requestGet(`models/${modelId}`, null, cancelSignal);
  getDtdlModel = (cancelSignal, modelId) => this.requestGet(`models/${modelId}/dtdl`, null, cancelSignal);
  deleteModel = (cancelSignal, modelId) => this.requestDelete(`models/${modelId}`, null, cancelSignal);
  deleteModelItem = (cancelSignal, item) => this.requestDelete(`models/${item.id}`, null, cancelSignal);

  getModelDrafts = (cancelSignal) => this.requestGet('models/drafts', null, cancelSignal);
  getModelDraft = (cancelSignal, modelId) => {
    return this.requestGet(`models/drafts/${modelId}`, null, cancelSignal);
  };
  updateModelDraft = (cancelSignal, modelData) => {
    return this.requestPut(`models/drafts/${modelData.id}`, modelData, cancelSignal);
  };
  deleteModelDraftItem = (cancelSignal, item) => this.requestDelete(`models/drafts/${item.id}`, null, cancelSignal);
  deleteModelDraft = (cancelSignal, modelId) => this.requestDelete(`models/drafts/${modelId}`, null, cancelSignal);
  createModelDraft = (cancelSignal, modelData) => this.requestPost('models/drafts', modelData, cancelSignal);
  promoteModelDraft = (cancelSignal, modelData) => {
    const { modelId, orgId } = modelData;
    return this.requestPost(`models/drafts/${encodeURIComponent(modelId)}/promote?organizationId=${orgId}`, null, cancelSignal);
  };

  getBindings = (cancelSignal) => this.requestGet('bindings', null, cancelSignal);
  createBinding = (cancelSignal, bindingData) => this.requestPost('bindings', bindingData, cancelSignal);
  getBinding = (cancelSignal, bindingId) => this.requestGet(`bindings/${bindingId}`, null, cancelSignal);
  deleteBinding = (cancelSignal, bindingId) => this.requestDelete(`bindings/${bindingId}`, null, cancelSignal);
  deleteBindingItem = (cancelSignal, item) => this.requestDelete(`bindings/${item.id}`, null, cancelSignal);

  getBindingsByModelId = (cancelSignal, modelId) => this.requestGet(`bindings/model/${encodeURIComponent(modelId)}`, null, cancelSignal);
  generateBindings = (cancelSignal, genData) => this.requestPost(`bindings/generate/${genData.modelId}/${genData.endpointId}`, null, cancelSignal);

  getBindingDrafts = (cancelSignal) => this.requestGet('bindings/drafts', null, cancelSignal);
  getBindingDraft = (cancelSignal, bindingId) => this.requestGet(`bindings/drafts/${bindingId}`, null, cancelSignal);
  createBindingDraft = (cancelSignal, bindingData) => this.requestPost('bindings/drafts', bindingData, cancelSignal);
  updateBindingDraft = (cancelSignal, bindingData) => this.requestPut(`bindings/drafts/${bindingData.id}`, bindingData, cancelSignal);
  deleteBindingDraft = (cancelSignal, bindingId) => this.requestDelete(`bindings/drafts/${bindingId}`, null, cancelSignal);
  deleteBindingDraftItem = (cancelSignal, item) => this.requestDelete(`bindings/drafts/${item.id}`, null, cancelSignal);
  promoteBindingDraft = (cancelSignal, bindingData) => {
    const { bindingId, orgId } = bindingData;
    return this.requestPost(`bindings/drafts/${encodeURIComponent(bindingId)}/promote?organizationId=${orgId}`, null, cancelSignal);
  };


  createNode = (cancelSignal, nodeData) => this.requestPost('graph/nodes', nodeData, cancelSignal);
  getNode = (cancelSignal, nodeId) => this.requestGet(`graph/nodes/${nodeId}`, null, cancelSignal);
  updateNode = (cancelSignal, nodeData) => this.requestPatch(`graph/nodes/${nodeData.id}`, nodeData, cancelSignal);
  deleteNode = (cancelSignal, nodeId) => this.requestDelete(`graph/nodes/${nodeId}`, null, cancelSignal);

  createEdge = (cancelSignal, edgeData) => this.requestPost('graph/edges', edgeData, cancelSignal);
  findEdges = (cancelSignal, edgeData) => this.requestPost('graph/edges/find', edgeData, cancelSignal);
  deleteEdge = (cancelSignal, edgeId) => this.requestDelete(`graph/edges/${edgeId}`, null, cancelSignal);

  getTrees = (cancelSignal) => this.requestGet('graph/trees', null, cancelSignal);
  getTreeFromNode = (cancelSignal, nodeId) => this.requestGet(`graph/trees/${nodeId}`, null, cancelSignal);

  getAlarms = (cancelSignal) => {
    return this.requestGet('alarms', null, cancelSignal);
  };
  getAlarm = (cancelSignal, alarmId) => this.requestGet(`alarms?alarmId=${alarmId}`, null, cancelSignal);
  createAlarm = (cancelSignal, alarmData) => this.requestPost('alarms', alarmData, cancelSignal);
  updateAlarm = (cancelSignal, alarmData) => this.requestPut(`alarms?alarmId=${alarmData.id}`, alarmData, cancelSignal);
  deleteAlarm = (cancelSignal, alarmId) => this.requestDelete(`alarms?alarmId=${alarmId}`, null, cancelSignal);
  getAlarmDefinitionFull = (cancelSignal, alarmId) => {
    const alarmDefPromise = this.getAlarm(cancelSignal, alarmId);
    const modelsPromise = this.getModels(cancelSignal);
    return Promise.all([alarmDefPromise, modelsPromise]).then(results => {
      const [alarmEntity, assetModels] = results;
      let promise;
      if (!_.isEmpty(alarmEntity.entityIds)) {
        promise = this.getAsset(cancelSignal, alarmEntity.entityIds[0]);
        return Promise.all([promise]).then(result => {
          const [asset] = result;
          const assetModelEnvelope = _.filter(assetModels, model => model.id === asset.modelId)[0];
          const modelPromise = this.getModel(cancelSignal, assetModelEnvelope.id);
          const assetPromise = this.getAssetsByModel(cancelSignal, assetModelEnvelope.id);
          return Promise.all([modelPromise, assetPromise]).then(result => {
            const [assetModel, assets] = result;
            return { assetModels, alarmEntity: { ...alarmEntity, entityTypeId: assetModelEnvelope.id }, assetModel, assets };
          });
        });
      }
      const assetModelEnvelope = _.filter(assetModels, model => model.id === alarmEntity.entityTypeId)[0];
      const modelPromise = this.getModel(cancelSignal, assetModelEnvelope.id);
      const assetPromise = this.getAssetsByModel(cancelSignal, assetModelEnvelope.id);
      return Promise.all([modelPromise, assetPromise]).then(result => {
        const [assetModel, assets] = result;
        return { assetModels, alarmEntity: { ...alarmEntity, entityTypeId: assetModelEnvelope.id }, assetModel, assets };
      });
    });
  };

  getAlarmStatus = (cancelSignal) => this.requestGet('alarms/status', null, cancelSignal);
  getAlarmStatusByAsset = (cancelSignal, assetId) => this.requestGet(`alarms/status/entity/${assetId}`, null, cancelSignal);
  acknowledgeAlarmStatus = (cancelSignal, alarmStatusId) => this.requestPut(`alarms/status/acknowledge/${alarmStatusId}`, null, cancelSignal);

  /**
   * @typedef {object} Channel
   * @property {boolean} active
   * @property {string} template
   *
   * @typedef {object} SubscriberGroup
   * @property {string} contactGroupId
   *
   * @typedef {object} Notification
   * @property {string} id
   * @property {string} displayName
   * @property {string} description
   * @property {boolean} broadcast
   * @property {object} trigger
   * @property {Array<string>} trigger.alarmTrigger
   * @property {object} channels
   * @property {Channel} channels.email
   * @property {Channel} channels.sms
   * @property {string} workdlowId,
   * @property {string} triggerIdentifier
   * @property {object} subscribers
   * @property {Array<SubscriberGroup>} subscribers.contactGroups
  */
  /**
   * @typedef {Notification} NotificationData
   * @property {string} organizationId


  * @param {*} cancelSignal
  * @param {NotificationData} notificationData - object with notification data
  *
  * @returns {Notification}
  */
  createNotification = (cancelSignal, notificationData) => this.requestPost('notifications', notificationData, cancelSignal);

  /**
   * @param {*} cancelSignal
   * @returns {Array<Notification>}
   * @memberof CoreApi
   */
  getNotifications = (cancelSignal) => this.requestGet('notifications', null, cancelSignal);
  /**
   * @param {*} cancelSignal
   * @param {string} notificationId - id of the notification
   * @returns {Notification}
   * @memberof CoreApi
   */
  getNotification = (cancelSignal, notificationId) => this.requestGet(`notifications/${notificationId}`, null, cancelSignal);
  /**
   * @param {*} cancelSignal
   * @param {string} notificationId - id of the notification
   * @returns {Notification}
   * @memberof CoreApi
   */
  updateNotification = (cancelSignal, notificationData) => this.requestPatch(`notifications/${notificationData.id}`, notificationData, cancelSignal);
  /**
   * @param {*} cancelSignal
   * @param {string} notificationId - id of the notification
   * @memberof CoreApi
   */
  deleteNotification = (cancelSignal, notificationId) => this.requestDelete(`notifications/${notificationId}`, null, cancelSignal);
  /**
   * @param {*} cancelSignal
   * @param {string} notificationId - id of the notification
   * @param {SubscriberGroup} subscriberData - object with subscriber data
   * @memberof CoreApi
   * @returns {object} subscriber
   */
  addNotificationSubscriber = (cancelSignal, notificationId, subscriberData) => this.requestPost(`notifications/${notificationId}/subscribers`, subscriberData, cancelSignal);
  /**
   * @param {*} cancelSignal
   * @param {string} notificationId - id of the notification
   * @param {string} subscriberId - id of the subscriber
   * @memberof CoreApi
   */
  deleteNotificationSubscriber = (cancelSignal, notificationId, subscriberId) => this.requestDelete(`notifications/${notificationId}/subscribers/${subscriberId}`, null, cancelSignal);

  /**
   * @typedef {object} ContactGroup
   * @property {string} displayName
   * @property {string} description
   * @property {Array<string>} users
   * @property {Array<string>} groups
   */
  createContactGroup = (cancelSignal, contactGroupData) => this.requestPost('contactgroups', contactGroupData, cancelSignal);
  getContactGroups = (cancelSignal) => this.requestGet('contactgroups', null, cancelSignal);
  getContactGroup = (cancelSignal, contactGroupId) => this.requestGet(`contactgroups/${contactGroupId}`, null, cancelSignal);
  updateContactGroup = (cancelSignal, contactGroupData) => this.requestPatch(`contactgroups/${contactGroupData.id}`, contactGroupData, cancelSignal);

  /**
   * @typedef {object} Timespan
   * @property {string} from
   * @property {string} to
   *
   * @typedef {object} AssetSignalSet
   * @property {string} assetId
   * @property {Array<object>} signals
  */
  /**
   * @typedef {object} AssetTimeseriesItem
   * @property {string} laneNumber
   * @property {string} color
   * @property {number} decimalPoint
   * @property {string} type
   * @property {string} fullName
   * @property {string} assetId
   * @property {string} name
   * @property {Array<Array<number>>} data
   *
   * @param {*} cancelSignal
   * @param {Array<AssetSignalSet} assetSignalSet
   * @param {Timespan} timespan
   * @param {string} aggregate
   * @returns {Array<AssetTimeseriesItem>}
   * @memberof CoreApi
   */
  getAssetsTimeSeries = async (cancelSignal, assetSignalsSet, timespan, aggregate) => {
    const requests = _.map(assetSignalsSet, assetSignals => {
      return this.requestPost('/timeseries/get', createAggregatedRequestData(assetSignals, timespan, aggregate), cancelSignal);
    });

    return Promise.allSettled(requests)
      .then(results => _.flatMap(results, (result, index) => convertTsiDataToGraphData(result, assetSignalsSet[index])));
  };
}

export default CoreApi;
